import React, { useEffect, useRef, useState } from 'react';
import { Button, Box } from '@mui/material';
import { getAccessToken } from 'src/services/auth';
import { OAUTH_SCOPES_NOT_OBTAINED } from 'src/constants/errors';
import { SUPPORTED_GOOGLE_DRIVE_MIME_TYPES } from 'src/constants/memory';

export interface FileData {
  id: string;
  name: string;
  url: string;
  mimeType: string;
}

interface GooglePickerProps {
  onFilesSelected?: (files: FileData[]) => void | Promise<void>;
  onConsentRequired?: () => void;
  onCancel?: () => void;
  autoPickFileId?: string | null;
  autoPickEnabled?: boolean;
  onAutoPickComplete?: (result: { success: boolean; message?: string }) => void;
  multiSelectEnabled?: boolean;
  selectableMimeTypes?: readonly string[];
}

const GooglePickerComponent: React.FC<GooglePickerProps> = ({
  onFilesSelected,
  onConsentRequired,
  onCancel,
  autoPickFileId,
  autoPickEnabled = false,
  onAutoPickComplete,
  multiSelectEnabled = true,
  selectableMimeTypes = SUPPORTED_GOOGLE_DRIVE_MIME_TYPES,
}) => {
  const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

  const PICKER_ORIGIN = window.location.origin;

  if (!API_KEY) {
    throw new Error(
      'Google API key is missing. Please set the environment variable.',
    );
  }

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [pickerAppId, setPickerAppId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pickerReady, setPickerReady] = useState(false);
  const [autoOpenAttempted, setAutoOpenAttempted] = useState(false);

  const lifecycleGeneration = useRef(0);
  const activePicker = useRef<{
    setVisible: (visible: boolean) => void;
    dispose?: () => void;
  } | null>(null);

  const getGapi = () => (window as any).gapi;

  const waitForGapi = async (attempts = 20, intervalMs = 250) => {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      const gapiInstance = getGapi();

      if (gapiInstance) {
        return gapiInstance;
      }

      await new Promise((resolve) => {
        window.setTimeout(resolve, intervalMs);
      });
    }

    return null;
  };

  const fetchAccessToken = async () => {
    try {
      const { success, content, message } = await getAccessToken();

      if (!success || !content) {
        if (message === OAUTH_SCOPES_NOT_OBTAINED) {
          onConsentRequired?.();
        } else {
          setErrorMessage('Error fetching access token.');
        }

        return;
      }

      setAccessToken(content.accessToken);
      setPickerAppId(content.pickerAppId);
    } catch {
      setErrorMessage(
        'An unexpected error occurred while fetching the access token.',
      );
    }
  };

  useEffect(() => {
    lifecycleGeneration.current += 1;
    fetchAccessToken();

    const loadGapi = () => {
      const initializeIfAvailable = async () => {
        const gapiInstance = await waitForGapi();

        if (!gapiInstance) {
          setErrorMessage('Google Picker API not available.');

          return;
        }

        gapiInstance.load('client:picker', initializePicker);
      };

      if (
        document.querySelector(
          'script[src="https://apis.google.com/js/api.js"]',
        )
      ) {
        void initializeIfAvailable();

        return;
      }

      const script = document.createElement('script');

      script.src = 'https://apis.google.com/js/api.js';
      script.async = true;

      script.onload = () => {
        void initializeIfAvailable();
      };

      script.onerror = () => {
        setErrorMessage('Failed to load Google Picker API.');
      };

      document.body.appendChild(script);
    };

    loadGapi();

    return () => {
      // Picker lives outside React's DOM. Close it and invalidate in-flight
      // authorization so an old route cannot open a dialog after unmount.
      lifecycleGeneration.current += 1;
      activePicker.current?.setVisible(false);
      activePicker.current?.dispose?.();
      activePicker.current = null;
    };
  }, []);

  useEffect(() => {
    setAutoOpenAttempted(false);
  }, [autoPickEnabled, autoPickFileId]);

  const initializePicker = async () => {
    const gapiInstance = getGapi();

    if (!gapiInstance?.client) {
      setErrorMessage('Google Picker API not available.');

      return;
    }

    try {
      await gapiInstance.client.load(
        'https://www.googleapis.com/discovery/v1/apis/drive/v3/rest',
      );
      setPickerReady(true);
    } catch (error) {
      setErrorMessage('Error initializing Google Picker.');
    }
  };

  const handleAuthClick = () => {
    // Retain the requested file restriction on manual retry as well as auto
    // opening; otherwise the retry can grant access to an unrelated file.
    createPicker(autoPickFileId);
  };

  const createPicker = async (preselectedFileId?: string | null) => {
    if (!accessToken || !pickerAppId) {
      setErrorMessage('Google Picker authorization is missing.');

      return;
    }

    const googlePicker = (window as any).google;
    const gapiInstance = getGapi();

    if (!googlePicker?.picker || !gapiInstance?.client) {
      setErrorMessage('Google Picker API not loaded.');

      return;
    }

    const generation = lifecycleGeneration.current;

    try {
      gapiInstance.client.setToken({ access_token: accessToken });
      await gapiInstance.client.drive.files.list({ pageSize: 1 });
    } catch (error: any) {
      if (lifecycleGeneration.current !== generation) return;
      const status = error?.status ?? error?.result?.error?.code;

      if ((status === 401 || status === 403) && onConsentRequired) {
        onConsentRequired();
      } else {
        setErrorMessage('Error authorizing Google Picker.');
      }

      return;
    }

    if (lifecycleGeneration.current !== generation) return;

    const mimeTypes = selectableMimeTypes.join(',');
    const docsView = new googlePicker.picker.DocsView()
      .setIncludeFolders(false)
      .setSelectFolderEnabled(false)
      .setMimeTypes(mimeTypes);

    if (preselectedFileId) {
      docsView.setFileIds(preselectedFileId);
    }

    const pickerBuilder = new googlePicker.picker.PickerBuilder()
      .enableFeature(googlePicker.picker.Feature.NAV_HIDDEN)
      .setDeveloperKey(API_KEY)
      // The server returns this beside the OAuth token so Picker registers the
      // `drive.file` grant under the exact project that issued that token.
      .setAppId(pickerAppId)
      .setOAuthToken(accessToken)
      .addView(docsView)
      .setSelectableMimeTypes(mimeTypes)
      .setOrigin(PICKER_ORIGIN)
      .setCallback((data: unknown) => {
        if (lifecycleGeneration.current === generation) pickerCallback(data);
      });
    const picker = (
      multiSelectEnabled
        ? pickerBuilder.enableFeature(
            googlePicker.picker.Feature.MULTISELECT_ENABLED,
          )
        : pickerBuilder
    ).build();

    activePicker.current?.setVisible(false);
    activePicker.current?.dispose?.();
    activePicker.current = picker;
    picker.setVisible(true);

    if (preselectedFileId) {
      onAutoPickComplete?.({ success: true });
    }
  };

  useEffect(() => {
    if (
      !autoPickEnabled ||
      autoOpenAttempted ||
      !autoPickFileId ||
      !pickerReady ||
      !accessToken ||
      !pickerAppId
    ) {
      return;
    }

    setAutoOpenAttempted(true);
    createPicker(autoPickFileId).catch(() => {
      setErrorMessage('Unable to open the picker automatically.');
      onAutoPickComplete?.({
        success: false,
        message: 'Auto-open failed',
      });
    });
  }, [
    autoPickEnabled,
    autoOpenAttempted,
    autoPickFileId,
    pickerReady,
    accessToken,
    pickerAppId,
  ]);

  const pickerCallback = (data: any) => {
    const googlePicker = (window as any).google;

    if (data.action === googlePicker.picker.Action.PICKED) {
      const docs = data[googlePicker.picker.Response.DOCUMENTS];

      if (docs && docs.length > 0) {
        const files: FileData[] = docs.map((doc: any) => ({
          id: doc[googlePicker.picker.Document.ID],
          name: doc[googlePicker.picker.Document.NAME] ?? '',
          url: doc[googlePicker.picker.Document.URL] ?? '',
          mimeType: doc[googlePicker.picker.Document.MIME_TYPE] ?? '',
        }));

        if (onFilesSelected) {
          onFilesSelected(files);
        }
      }
    } else if (data.action === googlePicker.picker.Action.CANCEL) {
      onAutoPickComplete?.({
        success: false,
        message: 'Picker closed without selecting a file.',
      });
      onCancel?.();
    }
  };

  return (
    <Box sx={{ padding: '0.5rem', display: 'flex', alignItems: 'center' }}>
      {errorMessage && (
        <Box component="p" sx={{ color: 'red', margin: 0, marginRight: 2 }}>
          {errorMessage}
        </Box>
      )}
      <Button
        variant="contained"
        color="primary"
        onClick={handleAuthClick}
        disabled={!accessToken}
        size="small"
        sx={{
          minWidth: '140px',
          py: 0.5,
          px: 1.5,
        }}
      >
        {accessToken ? 'Pick File' : 'Loading...'}
      </Button>
    </Box>
  );
};

export default GooglePickerComponent;
